import { useAuth } from "@/contexts/AuthContext";
import { FirmServiceV2 } from "@/services/firmServiceV2";
import type { Firm } from "@/services/firmServiceV2.types";
import { useMemo } from "react";
import useSWR, { mutate } from "swr";

import { useAPI } from "./useAPI";

export type { Firm } from "@/services/firmServiceV2.types";

const FIRMS_KEY = "/firms";

export const useFirms = () => {
  const api = useAPI("v2");
  const firmService = useMemo(() => new FirmServiceV2(api), [api]);
  const { user } = useAuth();

  const cacheKey = user?.id ? [FIRMS_KEY, user.id] : null;

  const { data, error, isLoading } = useSWR<Firm[]>(cacheKey, () => firmService.getFirmsForUser(), {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: 30 * 60 * 1000, // 30 minutes
  });

  return {
    firms: data ?? [],
    isLoading,
    isError: Boolean(error),
  };
};

export const invalidateFirmsCache = (userId: string): void => {
  if (!userId) return;
  mutate(
    (key: unknown) => Array.isArray(key) && key[0] === FIRMS_KEY && key[1] === userId,
    undefined,
    { revalidate: true }
  );
};
